<template>
  <section class="ApplicationOIDCFields">
    <ParagraphBody>
      To learn more about this configuration,
      including restricting access to specific users, please visit
      <Link
        :href="kbArticleUrl"
        target="_blank"
        text="Single Sign On (SSO) with OpenID Connect (OIDC)."
      />
    </ParagraphBody>

    <fieldset class="fieldset">
      <legend>
        <HeadingSmall
          class="heading"
          element="h5"
        >
          Endpoint Configuration
        </HeadingSmall>
      </legend>

      <section
        v-if="isItemBeingCreated"
        class="grantTypes"
      >
        <InputLabel
          name="oidc-chkAuthCode"
          text="Grant Types"
        />

        <Checkbox
          id="oidc-chkAuthCode"
          ref="authCode"
          isDisabled
          label="Authorization Code"
          :value="true"
        />
      </section>

      <CopyWell
        v-if="isItemBeingUpdated"
        ref="copyClientId"
        ariaLabel="Client ID (readonly)"
        helperText="
          The client secret of the application registered
          in the third-party OIDC identity provider.
        "
        label="Client ID"
        name="client-id"
        :value="model.sso.oidc.clientId || item.sso.oidc.clientId"
      />

      <section>
        <header>
          <InputLabel
            name="oidc-redirectUris"
            required
            text="Redirect URIs"
          />

          <ParagraphBody class="redirectUriHelperText">
            JumpCloud sends the authentication response and ID token
            for the user's sign-in request to these URIs.
          </ParagraphBody>
        </header>

        <div
          id="oidc-redirectUris"
          aria-live="polite"
          class="redirectUriContainer"
          role="region"
        >
          <div
            v-for="_, index in model.sso.oidc.redirectUris"
            :key="index"
            class="redirectUriItem"
          >
            <InputText
              ref="redirectUri"
              v-model="model.sso.oidc.redirectUris[index]"
              ariaLabel="Redirect URI"
              class="redirectUriInput"
              :name="`redirect-uri-${index}`"
              required
              rules="required|url-secure"
            />

            <IconButton
              ref="btnRemoveUri"
              aria-controls="oidc-redirectUris"
              :ariaLabel="`Remove redirect URI ${index + 1}`"
              data-test-id="RedirectUri"
              :disabled="model.sso.oidc.redirectUris.length === 1"
              hasNoBackground
              icon="delete"
              informational
              :isDisabled="model.sso.oidc.redirectUris.length === 1"
              @click="() => removeRedirectUri(index)"
            />
          </div>

          <IconButton
            ref="btnAddUri"
            aria-controls="redirectUris"
            class="redirectUriAdd"
            data-test-id="AddUri"
            hasNoBackground
            icon="add"
            informational
            matchText
            text="Add URI"
            @click="addRedirectUri"
          />
        </div>
      </section>

      <section class="authType">
        <div class="labelWrapper">
          <InputLabel
            class="lblAuthType"
            name="tea-method"
            text="Client Authentication Type"
          />

          <TooltipIcon
            id="oidc-authTypeTooltip"
            class="tooltip"
            icon="info"
            isMarkdown
            orientation="right"
            :text="`The client authentication type will depend on what is supported by the relying
              party.
              For more information, read
              [Single Sign-On with OpenID Connect (OIDC)](${kbArticleUrl})`"
          />
        </div>

        <RadioGroup
          v-model="model.sso.oidc.tokenEndpointAuthMethod"
          aria-describedby="oidc-authTypeTooltip"
          class="rdoAuthType"
          :isDisabled="isItemBeingUpdated"
          name="tea-method"
          :options="tokenEndpointAuthMethodOptions"
        />
      </section>

      <InputText
        ref="loginUrl"
        v-model="model.sso.oidc.relyingPartyUrl"
        helperText="Enter the URL users need to log into this application"
        label="Login URL"
        name="login-url"
        required
        rules="required|url"
      />
    </fieldset>

    <fieldset class="fieldset">
      <legend>
        <HeadingSmall
          class="heading"
          element="h5"
        >
          Attribute Mapping (optional)
        </HeadingSmall>

        <span>
          If attributes are required by this Service Provider
          for SSO authentication, they are not editable.
          Additional attributes may be included in assertions,
          although support for each attribute will vary for
          each Service Provider.
          <Link
            :href="kbArticleUrl"
            target="_blank"
            text="Learn More."
          />
        </span>
      </legend>

      <section class="attributeSection">
        <h6 class="attributeHeading">
          USER ATTRIBUTE MAPPING:
          <TooltipIcon
            id="oidc-userAttributeTooltip"
            class="tooltip"
            icon="info"
            isMarkdown
            orientation="right"
            :text="`Configure any user-specific attributes to be sent to the service provider.
              The value specified must directly reflect the JumpCloud system user property name
              (e.g., email, firstname, lastname).
              See the [Knowledge Base](${kbArticleUrl}) for exact JumpCloud property names.`"
          />
        </h6>

        <div
          v-show="!!model.sso.oidc.dynamicClaims.length"
          id="oidc-userAttributes"
          aria-describedby="oidc-userAttributeTooltip"
          :aria-hidden="!model.sso.oidc.dynamicClaims.length"
          aria-label="User Attributes"
          aria-live="polite"
          role="region"
        >
          <div class="attributeRow">
            <InputLabel
              class="attributeLabel"
              required
              text="Service Provider Attribute Name"
            />
            <InputLabel
              class="attributeLabel"
              required
              text="JumpCloud Attribute Name"
            />
            <div class="btnRemoveAttribute" />
          </div>
          <fieldset
            v-for="(item, i) in model.sso.oidc.dynamicClaims"
            :key="`spAttr-${i}`"
            class="attributeRow"
          >
            <InputText
              ref="txtUserSPName"
              v-model="model.sso.oidc.dynamicClaims[i].name"
              ariaLabel="Service Provider Attribute Name"
              autofocus
              class="attributeInput"
              :name="`sp-name-${i}`"
              required
            />

            <!-- @todo: This should be a combobox but there isn't one available in ui-components -->
            <InputText
              v-show="item.isCustomOption"
              id="oidc-jcUserAttrName"
              ref="txtCustomName"
              v-model="model.sso.oidc.dynamicClaims[i].value"
              :aria-hidden="!item.isCustomOption"
              ariaLabel="JumpCloud Attribute Name"
              :autofocus="item.isCustomOption"
              class="inputWithAction attributeInput"
              :focus="item.isCustomOption"
              :isDisabled="!item.isCustomOption"
              :name="`jc-name-text-${i}`"
              required
            >
              <template v-slot:action>
                <IconButton
                  ref="btnClearCustomAttr"
                  aria-controls="oidc-jcUserAttrName"
                  ariaLabel="Clear Custom Attribute"
                  class="inputAction"
                  hasNoBackground
                  icon="close"
                  informational
                  :isDisabled="!item.isCustomOption"
                  :scale="0.5"
                  @click="() => clearCustomAttribute(i)"
                />
              </template>
            </InputText>

            <Select
              v-show="!item.isCustomOption"
              ref="selJCName"
              v-model="model.sso.oidc.dynamicClaims[i].value"
              :aria-hidden="item.isCustomOption"
              ariaLabel="JumpCloud Attribute Name"
              :autofocus="!item.isCustomOption"
              class="attributeInput"
              :focus="!item.isCustomOption"
              :isDisabled="item.isCustomOption"
              :name="`jc-name-select-${i}`"
              :options="jcAttributeOptions"
              required
              @input="value => onAttrSelect(value, i)"
            />

            <IconButton
              ref="btnRemoveUserAttr"
              aria-controls="oidc-userAttributes"
              :ariaLabel="`Remove User Attribute ${i + 1}`"
              class="btnRemoveAttribute"
              hasNoBackground
              icon="delete"
              informational
              @click="() => removeUserAttribute(i)"
            />
          </fieldset>
        </div>

        <Button
          ref="btnAddUserAttr"
          aria-controls="userAttributes"
          aria-label="Add User Attribute"
          text="Add Attribute"
          @click="addUserAttribute"
        />
      </section>

      <section class="attributeSection">
        <h6 class="attributeHeading">
          CONSTANT ATTRIBUTES:
          <TooltipIcon
            id="oidc-constAttributeTooltip"
            class="tooltip"
            icon="info"
            orientation="right"
            text="Configure any constant-value attributes to be sent to the service provider. The
              values will be sent for all users."
          />
        </h6>

        <div
          v-show="!!model.sso.oidc.staticClaims.length"
          id="oidc-constAttributes"
          aria-describedby="oidc-constAttributeTooltip"
          :aria-hidden="!model.sso.oidc.staticClaims.length"
          aria-label="Constant Attributes"
          aria-live="polite"
          role="region"
        >
          <div class="attributeRow">
            <InputLabel
              class="attributeLabel"
              required
              text="Service Provider Attribute Name"
            />
            <InputLabel
              class="attributeLabel"
              required
              text="Value"
            />
            <div class="btnRemoveAttribute" />
          </div>
          <fieldset
            v-for="(item, i) in model.sso.oidc.staticClaims"
            :key="`spAttr-${i}`"
            class="attributeRow"
          >
            <InputText
              ref="txtConstSPName"
              v-model="model.sso.oidc.staticClaims[i].name"
              ariaLabel="Service Provider Attribute Name"
              autofocus
              class="attributeInput"
              name="name"
              required
            />

            <InputText
              ref="txtConstValue"
              v-model="model.sso.oidc.staticClaims[i].value"
              ariaLabel="Value"
              class="attributeInput"
              name="value"
              required
            />

            <IconButton
              ref="btnRemoveConstAttr"
              aria-controls="oidc-constAttributes"
              :ariaLabel="`Remove Constant Attribute ${i + 1}`"
              class="btnRemoveAttribute"
              hasNoBackground
              icon="delete"
              informational
              @click="() => removeConstAttribute(i)"
            />
          </fieldset>
        </div>

        <Button
          ref="btnAddConstAttr"
          aria-controls="oidc-constAttributes"
          aria-label="Add Constant Attribute"
          text="Add Attribute"
          @click="addConstAttribute"
        />
      </section>

      <section>
        <h6 class="attributeHeading">
          GROUP ATTRIBUTES
          <TooltipIcon
            id="oidc-groupsAttributeNameTooltip"
            class="tooltip"
            icon="info"
            orientation="right"
            text="Select this checkbox to include the groups that connect the user to the
              application in the OIDC claim."
          />
        </h6>

        <div class="groupsAttributeNameContainer">
          <Checkbox
            v-model="includeGroups"
            aria-controls="oidc-txtGroupsAttributeName"
            aria-describedby="oidc-groupsAttributeNameTooltip"
            class="chkGroupsAttributeName"
            label="include group attribute"
          />

          <InputText
            v-if="includeGroups"
            id="oidc-txtGroupsAttributeName"
            v-model="model.groupsName"
            class="txtGroupsAttributeName"
            name="groups-key"
            placeholder="memberOf"
            required
          />
        </div>
      </section>
    </fieldset>

    <section>
      <HeadingSmall class="heading">
        User Consent
      </HeadingSmall>

      <ParagraphBody>
        This application defaults to automatically consenting on behalf of users.
        Users will not be prompted to provide consent.
      </ParagraphBody>

      <Checkbox
        isDisabled
        label="Automatically consent"
        :value="true"
      />
    </section>
  </section>
</template>

<script>
import {
  Button,
  Checkbox,
  CopyWell,
  HeadingSmall,
  IconButton,
  InputLabel,
  InputText,
  Link,
  ParagraphBody,
  RadioGroup,
  Select,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import { customItem, databaseAttributes, oidcTokenEndpointAuthMethods } from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapGetters } from 'vuex';

const applicationModel = StoreModelMap[ItemTypeConsts.application];
const customItemOption = { name: customItem, value: 'custom' };

export default {
  name: 'ApplicationOIDCFields',

  components: {
    Button,
    Checkbox,
    CopyWell,
    HeadingSmall,
    IconButton,
    InputLabel,
    InputText,
    Link,
    ParagraphBody,
    RadioGroup,
    Select,
    TooltipIcon,
  },

  data() {
    return {
      includeGroups: false,
      jcAttributeOptions: [
        customItemOption,
        ...databaseAttributes.map(value => ({
          name: value,
          value,
        })),
      ],
      kbArticleUrl: 'https://support.jumpcloud.com/support/s/article/Single-Sign-On-SSO-with-OpenID-Connect-OIDC',
      model: {},
    };
  },

  computed: {
    ...mapGetters(applicationModel, [
      'isItemBeingCreated',
      'isItemBeingUpdated',
      'item',
    ]),

    tokenEndpointAuthMethodOptions() {
      return [{
        disabled: this.isItemBeingUpdated,
        label: 'Client Secret Post',
        value: oidcTokenEndpointAuthMethods.clientSecretPOST,
      }, {
        disabled: this.isItemBeingUpdated,
        label: 'Client Secret Basic',
        value: oidcTokenEndpointAuthMethods.clientSecretBasic,
      }, {
        disabled: this.isItemBeingUpdated,
        label: 'Public (None PKCE)',
        value: oidcTokenEndpointAuthMethods.none,
      }];
    },
  },

  watch: {
    model: {
      deep: true,
      handler() {
        const item = cloneDeep(this.model);

        // remove `isCustomOption` from db attrs
        item.sso.oidc.dynamicClaims = item.sso.oidc.dynamicClaims
          .map(({ name, value }) => ({ name, value }));

        if (this.includeGroups) {
          item.sso.oidc.dynamicClaims.push({
            name: item.groupsName,
            value: 'groups',
          });

          delete item.groupsName;
        }

        this.updateItem(item);
      },
    },
  },

  created() {
    const model = cloneDeep(this.item);

    if (this.isItemBeingCreated) {
      model.sso.oidc.redirectUris.push('https://');
      model.sso.oidc.relyingPartyUrl = 'https://';
    }

    const groupsAttributeIndex = model.sso.oidc.dynamicClaims.findIndex(({ value }) => value === 'groups');

    if (groupsAttributeIndex > -1) {
      this.includeGroups = true;
      model.groupsName = model.sso.oidc.dynamicClaims[groupsAttributeIndex].name;
      model.sso.oidc.dynamicClaims.splice(groupsAttributeIndex, 1);
    }

    model.sso.oidc.dynamicClaims = model.sso.oidc.dynamicClaims
      .map(({ name, value }) => (
        { name, value, isCustomOption: !databaseAttributes.includes(name) }
      ));

    if (this.isItemBeingCreated) {
      model.sso.oidc.tokenEndpointAuthMethod = oidcTokenEndpointAuthMethods.clientSecretPOST;
    }

    this.model = model;
  },

  methods: {
    ...mapActions(applicationModel, [
      'updateItem',
    ]),

    addConstAttribute() {
      this.model.sso.oidc.staticClaims = [...this.model.sso.oidc.staticClaims, {
        name: '',
        value: '',
      }];
    },

    addRedirectUri() {
      this.model.sso.oidc.redirectUris = [...this.model.sso.oidc.redirectUris, 'https://'];
    },

    addUserAttribute() {
      this.model.sso.oidc.dynamicClaims = [...this.model.sso.oidc.dynamicClaims, {
        name: '',
        value: '',
      }];
    },

    clearCustomAttribute(i) {
      this.$set(this.model.sso.oidc.dynamicClaims, i, {
        name: this.model.sso.oidc.dynamicClaims[i].name,
        value: '',
      });
    },

    onAttrSelect(value, i) {
      if (value === customItemOption.value) {
        this.$set(this.model.sso.oidc.dynamicClaims, i, {
          ...this.model.sso.oidc.dynamicClaims[i],
          value: '',
          isCustomOption: true,
        });
      }
    },

    removeConstAttribute(index) {
      this.model.sso.oidc.staticClaims.splice(index, 1);
    },

    removeRedirectUri(index) {
      this.model.sso.oidc.redirectUris.splice(index, 1);
    },

    removeUserAttribute(index) {
      this.model.sso.oidc.dynamicClaims.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.fieldset {
  margin-bottom: var(--jc-spacer-large);
}

.heading {
  margin-bottom: var(--jc-spacer-small);
}

.grantTypes {
  margin: var(--jc-spacer-small) 0 var(--jc-spacer);
}

.labelWrapper {
  align-items: center;
  display: flex;
  margin-bottom: var(--jc-spacer-small);
}

.authType {
  margin-bottom: var(--jc-spacer);
}

.ApplicationOIDCFields .lblAuthType {
  display: inline-block;
  margin-bottom: 0;
}

.rdoAuthType {
  display: inline-block;
}

.tooltip {
  margin-left: var(--jc-spacer-x-small);
  z-index: 2;

  /*
    for some reason tooltip z-index is set to 1
    which allows normal flow content to overlay it when open
  */
}

.redirectUriHelperText {
  color: var(--jc-color-neutral-text-descriptive);
}

.redirectUriContainer {
  padding: var(--jc-spacer-small) 0 var(--jc-spacer);
}

.redirectUriItem {
  align-items: baseline;
  display: flex;
  margin-bottom: var(--jc-spacer-small);
}

.redirectUriInput {
  flex: 1;
  margin-right: var(--jc-spacer);
}

.redirectUriAdd {
  color: var(--jc-informational-base);
}

.attributeHeading {
  font-size: 1rem;
  font-weight: normal;
  margin: var(--jc-spacer-small) 0;
}

.attributeSection {
  margin-bottom: var(--jc-spacer-medium);
}

.groupsAttributeNameContainer {
  align-items: baseline;
  display: flex;
}

.chkGroupsAttributeName {
  margin-right: var(--jc-spacer);
}

.txtGroupsAttributeName {
  flex: 1;
}

.attributeInput {
  flex: 1;
  margin-right: var(--jc-spacer);
}

.attributeLabel {
  flex: 1;
  margin-bottom: 0;
  margin-right: var(--jc-spacer);
}

.attributeRow {
  align-items: baseline;
  display: flex;
  margin-bottom: var(--jc-spacer-small);
}

.inputWithAction {
  position: relative;
}

.inputAction {
  font-size: 24px !important;
  position: absolute !important;
  right: 4px;
  top: 5px;
  z-index: 2;
}

.btnRemoveAttribute {
  width: 32px;
}
</style>
